import { useEffect, useState } from "react";
import api from "@/lib/api";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Pick } from "@/components/vi/SaleDialog";
import { call } from "@/lib/vi";

// Formulaire compact générique : fields = [{ name, label, type: text|number|select|textarea|password|checkbox, options }]
export function QuickForm({ open, onOpenChange, title, fields, initial, onSubmit, submitLabel = "Enregistrer", testid, children }) {
  const [f, setF] = useState({});
  useEffect(() => { if (open) setF(initial || {}); }, [open]); // eslint-disable-line react-hooks/exhaustive-deps
  const set = (k, v) => setF((p) => ({ ...p, [k]: v }));
  const submit = async () => { if (await onSubmit(f)) onOpenChange(false); };
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-lg max-h-[92vh] overflow-y-auto" data-testid={testid}>
        <DialogHeader><DialogTitle className="font-head">{title}</DialogTitle></DialogHeader>
        {children}
        <div className="space-y-3">
          {fields.map((fd) => (
            <div key={fd.name}>
              {fd.type === "checkbox" ? (
                <label className="flex items-start gap-2 text-sm"><input type="checkbox" className="mt-1" checked={!!f[fd.name]} onChange={(e) => set(fd.name, e.target.checked)} data-testid={`qf-${fd.name}`} />{fd.label}</label>
              ) : <>
                <Label className="text-xs">{fd.label}</Label>
                {fd.type === "select" ? <Pick value={f[fd.name]} onChange={(v) => set(fd.name, v)} testid={`qf-${fd.name}`} placeholder="Sélectionner" options={(fd.options || []).map((o) => (typeof o === "string" ? { value: o, label: o } : o))} />
                  : fd.type === "textarea" ? <Textarea className="mt-1" rows={2} value={f[fd.name] || ""} onChange={(e) => set(fd.name, e.target.value)} data-testid={`qf-${fd.name}`} />
                  : <Input className="mt-1" type={fd.type === "number" ? "number" : fd.type === "password" ? "password" : fd.type === "date" ? "date" : "text"} value={f[fd.name] ?? ""}
                      onChange={(e) => set(fd.name, fd.type === "number" ? Number(e.target.value) : e.target.value)} data-testid={`qf-${fd.name}`} />}
              </>}
            </div>
          ))}
        </div>
        <DialogFooter><Button variant="outline" onClick={() => onOpenChange(false)}>Annuler</Button>
          <Button onClick={submit} className="bg-brand-600 hover:bg-brand-700 text-white" data-testid={`${testid}-submit`}>{submitLabel}</Button></DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

export function ExpenseDialog({ tour, open, onOpenChange, onDone, cats }) {
  return <QuickForm open={open} onOpenChange={onOpenChange} title="Nouvelle dépense" testid="expense-dialog" initial={{ categorie: "Carburant", montant: 0 }}
    fields={[{ name: "categorie", label: "Catégorie", type: "select", options: cats }, { name: "montant", label: "Montant (FCFA)", type: "number" }, { name: "commentaire", label: "Commentaire", type: "textarea" }]}
    onSubmit={async (f) => { const r = await call(() => api.post(`/itinerant/tournees/${tour.id}/expenses`, f), "Dépense enregistrée"); if (r) onDone(); return !!r; }} />;
}

export function ReturnDialog({ tour, open, onOpenChange, onDone, motifs }) {
  const [clients, setClients] = useState([]);
  useEffect(() => { if (open) api.get("/itinerant/clients").then((r) => setClients(r.data)); }, [open]);
  const sales = (tour?.sales || []).filter((s) => s.statut === "Validée");
  const arts = (tour?.mobile_stock || []).map((s) => ({ value: s.article_id, label: s.designation }));
  return <QuickForm open={open} onOpenChange={onOpenChange} title="Retour marchandise" testid="return-dialog" initial={{ quantite: 1, etat: "Bon état", motif: "Retour commercial" }}
    fields={[{ name: "client_id", label: "Client", type: "select", options: clients.map((c) => ({ value: c.id, label: c.nom })) },
      { name: "sale_id", label: "Vente (facultatif)", type: "select", options: sales.map((s) => ({ value: s.id, label: `${s.numero} · ${s.client_name}` })) },
      { name: "article_id", label: "Article", type: "select", options: arts }, { name: "quantite", label: "Quantité", type: "number" },
      { name: "motif", label: "Motif", type: "select", options: motifs }, { name: "etat", label: "État du produit", type: "select", options: ["Bon état", "Endommagé", "Défectueux"] },
      { name: "commentaire", label: "Commentaire", type: "textarea" }]}
    onSubmit={async (f) => { const r = await call(() => api.post(`/itinerant/tournees/${tour.id}/returns`, f), "Retour enregistré (en attente de validation)"); if (r) onDone(); return !!r; }} />;
}

export function ProspectDialog({ open, onOpenChange, onDone }) {
  return <QuickForm open={open} onOpenChange={onOpenChange} title="Nouveau prospect" testid="prospect-quick-dialog" initial={{}}
    fields={[{ name: "entreprise", label: "Nom de la boutique" }, { name: "nom", label: "Responsable" }, { name: "telephone", label: "Téléphone" },
      { name: "whatsapp", label: "WhatsApp" }, { name: "adresse", label: "Localisation / adresse" }, { name: "type_prospect", label: "Type d'activité" },
      { name: "commentaires", label: "Produits vendus, fournisseur actuel, prix concurrents, besoins…", type: "textarea" }]}
    onSubmit={async (f) => {
      if (!f.nom && !f.entreprise) return false;
      const r = await call(() => api.post("/prospects", { ...f, nom: f.nom || f.entreprise, source: "Vente itinérante", stage: "Nouveau" }), "Prospect créé");
      if (r) onDone?.(); return !!r;
    }} />;
}
